import React, { useEffect, useState, useContext } from 'react';
import { toast } from 'react-toastify';
import Select, { StylesConfig } from 'react-select';
import api from '~/services/api';
import { MacroContext } from '~/context/macros';

interface IMacro {
  cod_item: number;
  valor: any;
  des_item: string;
}

type Option = { value: string; label: string };

interface MacroProps {
  tipoInfo: number;
  onChange(e: any): void;
  defaultValue: any;
}

const Macro: React.FC<MacroProps> = ({ tipoInfo, onChange, defaultValue }) => {
  const [macros, setMacros] = useState<IMacro[]>([]);
  const [options, setOptions] = useState<Option[]>([]);
  const [value, setValue] = useState<Option | null>(null);

  const { getMacro, addMacro } = useContext(MacroContext);

  const Z = 2000;

  const styles: StylesConfig<Option, false> = {
    container: (base) => ({ ...base, zIndex: Z }),
    control: (base, state) => ({
      ...base,
      minHeight: 38,
      borderRadius: 6,
      borderColor: state.isFocused ? '#8850bf' : base.borderColor,
      boxShadow: state.isFocused ? '0 0 0 2px rgba(136,80,191,0.20)' : 'none',
      ':hover': { borderColor: '#8850bf' },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#57069e'
        : state.isFocused
        ? 'rgba(136,80,191,0.08)'
        : 'transparent',
      color: state.isSelected ? '#fff' : '#1d1d1f',
      ':active': {
        backgroundColor: state.isSelected ? '#57069e' : 'rgba(136,80,191,0.12)',
      },
    }),
    menuPortal: (base) => ({ ...base, zIndex: Z }),
    menu: (base) => ({
      ...base,
      zIndex: Z,
      borderRadius: 8,
      overflow: 'hidden',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    singleValue: (b) => ({ ...b, fontWeight: 500 }),
    placeholder: (b) => ({ ...b, opacity: 0.8 }),
  };

  const theme: any = (t: any) => ({
    ...t,
    borderRadius: 6,
    colors: {
      ...t.colors,
      primary: '#8850bf',
      primary25: 'rgba(136,80,191,0.12)',
      primary50: 'rgba(136,80,191,0.20)',
      neutral30: '#8850bf',
    },
  });

  useEffect(() => {
    (async () => {
      try {
        const cached = getMacro(tipoInfo);
        if (cached) {
          setMacros(cached.items);
        } else {
          const { data } = await api.get(`/macros/${tipoInfo}`);
          if (data?.data) {
            setMacros(data.data);
            addMacro({ cod_tipo_info: tipoInfo, items: data.data });
          }
        }
      } catch (e: any) {
        toast.error(e?.data?.message ?? 'Falha ao carregar macros.');
      }
    })();
  }, [tipoInfo]);

  useEffect(() => {
    const opts = macros.map(({ des_item, cod_item }) => ({
      label: des_item,
      value: String(cod_item),
    }));
    setOptions(opts);
  }, [macros]);

  useEffect(() => {
    if (defaultValue !== '' && options.length) {
      const opt = options.find((o) => o.value === String(defaultValue)) || null;
      setValue(opt);
    }
  }, [defaultValue, options]);

  const handleChange = (opt: any) => {
    if (opt) {
      setValue(opt);
      onChange(opt);
    }
  };

  return (
    <Select
      placeholder="Selecione"
      options={options}
      className="single-select"
      classNamePrefix="react-select"
      onChange={handleChange}
      value={value}
      styles={styles}
      theme={theme}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      closeMenuOnScroll={false}
      isSearchable
      noOptionsMessage={() => 'Nenhum registro encontrado'}
    />
  );
};

export default Macro;
